import { useEffect, useState, useCallback } from "react";
import { X, Mail, Loader2, Copy, Trash2, ShieldPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUS_CLS = {
  pending: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  accepted: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  expired: "bg-neutral-700/40 text-neutral-400 border-neutral-600",
  revoked: "bg-red-500/10 text-red-300 border-red-500/30",
};

/** Invite a new admin by email — sends a one-time link (7-day expiry). */
export default function InviteAdminModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState("");
  const [invites, setInvites] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/admin-invites");
      setInvites(data.invites || []);
    } catch (_) { /* non-blocking */ }
  }, []);

  useEffect(() => { if (open) { setLastLink(""); load(); } }, [open, load]);
  if (!open) return null;

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/admin-invites", { email });
      toast.success(`Invite sent to ${data.email}`);
      setLastLink(data.invite_link);
      setEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send invite");
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/admin/admin-invites/${id}`);
      toast.success("Invite revoked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(lastLink); toast.success("Link copied"); }
    catch (_) { toast.error("Couldn't copy — select and copy manually"); }
  };

  return (
    <div data-testid="invite-admin-modal" role="dialog" aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-neutral-800 bg-neutral-950 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Admin team</p>
            <h3 className="mt-1 text-xl font-extrabold text-neutral-100">Invite an admin</h3>
            <p className="mt-1 text-xs text-neutral-400">
              They get a personal, single-use link by email (expires in 7 days). No shared codes.
            </p>
          </div>
          <button onClick={onClose} data-testid="invite-admin-close" aria-label="Close"
            className="rounded-md p-1 text-neutral-500 hover:bg-neutral-800 hover:text-neutral-200">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={send} className="mt-4 flex gap-2">
          <input type="email" required value={email} placeholder="colleague@company.com"
            data-testid="invite-admin-email"
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 placeholder:text-neutral-500 focus:border-[#F59E0B] focus:outline-none" />
          <button type="submit" disabled={busy}
            data-testid="invite-admin-send"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send invite
          </button>
        </form>

        {lastLink && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2" data-testid="invite-admin-link">
            <code className="flex-1 truncate text-[11px] text-emerald-300">{lastLink}</code>
            <button onClick={copyLink} data-testid="invite-admin-copy"
              className="rounded-md p-1.5 text-emerald-300 hover:bg-emerald-500/20" title="Copy link">
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <div className="mt-5 max-h-56 overflow-y-auto">
          {invites.length === 0 ? (
            <p className="text-xs text-neutral-500" data-testid="invite-admin-empty">No invites yet.</p>
          ) : invites.map((inv) => (
            <div key={inv.invite_id} className="flex items-center gap-2 border-b border-neutral-900 py-2"
              data-testid={`invite-row-${inv.invite_id}`}>
              <ShieldPlus className="h-3.5 w-3.5 shrink-0 text-neutral-600" />
              <span className="flex-1 truncate text-xs text-neutral-300">{inv.email}</span>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${STATUS_CLS[inv.status] || STATUS_CLS.expired}`}>
                {inv.status}
              </span>
              {inv.status === "pending" && (
                <button onClick={() => revoke(inv.invite_id)}
                  data-testid={`invite-revoke-${inv.invite_id}`}
                  className="rounded-md p-1 text-red-400 hover:bg-red-500/10" title="Revoke">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
